/**
 * 📈 The new-player funnel's vocabulary (v7.39, Javi's D13: "cookieless
 * counter + error beacon"). ONE list, imported by the client helper
 * (app/utils/funnel.ts) and the route (server/api/funnel.post.ts), so a
 * step the client can send is exactly a step the server will count.
 *
 * Adding a step: append it here (snake_case, order = funnel order - the
 * admin read lists steps in this order), then call track() where it
 * happens. Anything not on the list is dropped by the server with a 204.
 */
export const FUNNEL_STEPS = [
  'title_first_view',
  'welcome_shown',
  'welcome_yes',
  'welcome_played_before',
  'welcome_closed',
  'practice_start',
  'practice_lesson1_won',
  'practice_lesson2_done',
  'practice_done',
  'practice_skip',
  'draft_confirmed',
  'map_path_taken',
  'location1_cleared',
  'first_defeat',
  'act1_cleared',
  'error',
] as const
export type FunnelStep = typeof FUNNEL_STEPS[number]

export const FUNNEL_SIZES = ['phone', 'tablet', 'desktop'] as const
export type FunnelSize = typeof FUNNEL_SIZES[number]

/** Where an error was caught: window 'error', 'unhandledrejection', or Vue. */
export const FUNNEL_WHERE = ['onerror', 'rejection', 'vue'] as const
export type FunnelWhere = typeof FUNNEL_WHERE[number]

/** CURRENT_VERSION's shape (app/data/version.ts). Length-capped as well,
 * because the build becomes a field name in the day document. */
export const FUNNEL_BUILD_RE = /^v\d+\.\d+$/
export const FUNNEL_BUILD_MAX = 12
export const FUNNEL_MESSAGE_MAX = 160

export interface FunnelEvent {
  step: FunnelStep
  build: string
  size: FunnelSize
  /** Error beacon only (step 'error'). */
  message?: string
  where?: FunnelWhere
}

const STEP_SET = new Set<string>(FUNNEL_STEPS)
const SIZE_SET = new Set<string>(FUNNEL_SIZES)
const WHERE_SET = new Set<string>(FUNNEL_WHERE)

export function isFunnelStep(step: unknown): step is FunnelStep {
  return typeof step === 'string' && STEP_SET.has(step)
}

/**
 * An error message with anything that could identify a person or a
 * session taken out, cut to 160 characters. Runs on the client (keeps the
 * body small) AND on the server (the server never trusts the client's):
 *   - URLs of any scheme (http, chrome-extension, blob...) -> <url>
 *   - e-mail addresses -> <email>
 *   - query strings (`?key=value...`) -> removed
 *   - long tokens that mix letters and digits (uids, JWT segments,
 *     hashes; 16+ chars) -> <id>
 *   - runs of 4+ digits (ids, timestamps, ports, phone numbers) -> #
 * Plain identifiers survive ("ReferenceError: useOnboarding is not
 * defined" is the whole point), and so do short numbers.
 */
export function scrubFunnelMessage(raw: unknown): string {
  let s = typeof raw === 'string' ? raw : raw == null ? '' : String(raw)
  s = s.slice(0, 1000) // bounded work whatever arrives
  s = s
    .replace(/[\u0000-\u001f\u007f]+/g, ' ')
    .replace(/\b[a-z][a-z0-9+.-]*:\/\/[^\s"'`<>()]*/gi, '<url>')
    .replace(/\bwww\.[^\s"'`<>()]+/gi, '<url>')
    .replace(/[^\s@"'`<>(),;:]+@[^\s@"'`<>(),;:]+\.[a-z]{2,}/gi, '<email>')
    .replace(/\?[\w.%-]+=[^\s"'`<>()]*/g, '')
    .replace(/\b(?=[A-Za-z_-]*\d)(?=[\d_-]*[A-Za-z])[A-Za-z0-9_-]{16,}\b/g, '<id>')
    .replace(/\d{4,}/g, '#')
    .replace(/\s+/g, ' ')
    .trim()
  return s.length > FUNNEL_MESSAGE_MAX ? s.slice(0, FUNNEL_MESSAGE_MAX) : s
}

/**
 * The route's gate: a well-formed event, or null (which the route answers
 * with a silent 204 - never an error, so an old tab sending a retired step
 * shows nothing in the player's console). Extra body fields are ignored;
 * message/where are kept only for the error beacon.
 */
export function parseFunnelEvent(body: unknown): FunnelEvent | null {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null
  const b = body as Record<string, unknown>
  if (!isFunnelStep(b.step)) return null
  if (typeof b.build !== 'string' || b.build.length > FUNNEL_BUILD_MAX || !FUNNEL_BUILD_RE.test(b.build)) return null
  if (typeof b.size !== 'string' || !SIZE_SET.has(b.size)) return null
  const ev: FunnelEvent = { step: b.step, build: b.build, size: b.size as FunnelSize }
  if (b.step === 'error') {
    if (typeof b.where !== 'string' || !WHERE_SET.has(b.where)) return null
    ev.where = b.where as FunnelWhere
    ev.message = scrubFunnelMessage(b.message) || '(no message)'
  }
  return ev
}
